import { NativeError } from '@belimo-retrofit-portal/logic';
import { replace } from 'redux-first-history';
import { SagaIterator } from 'redux-saga';
import { INTEGER_ONE } from 'src/modules/common/constants/integer';
import { sentryCatch } from 'src/modules/config/utils/sentryCatch';
import { getLoggedInUser } from 'src/modules/login/selectors/getLoggedInUser';
import { showNotification } from 'src/modules/notifications/sagas/showNotification';
import {
  PROJECT_LIST_FETCH,
  PROJECT_LIST_UNFOLLOW_SUBMIT,
} from 'src/modules/project-list/actions/ProjectListActions';
import { loadProjectList } from 'src/modules/project-list/sagas/utils/loadProjectList';
import { unfollowProject } from 'src/modules/project-list/sagas/utils/unfollowProject';
import { getProjectListQuery } from 'src/modules/project-list/selectors/getProjectListQuery';
import { getProjectListUnfollowing } from 'src/modules/project-list/selectors/getProjectListUnfollowing';
import { ProjectListData } from 'src/modules/project-list/types/ProjectListData';
import { buildProjectListQuery } from 'src/modules/project-list/utils/query';
import { applyProjectListQuery } from 'src/modules/project-list/utils/slice';
import { navigate } from 'src/modules/routing/utils/navigate';
import { logDebug, logError } from 'src/sagas/utils/logging';
import { assertNotNull } from 'src/utils/assert';
import { call, put, select } from 'typed-redux-saga';

export function* projectListUnfollowSaga(): SagaIterator<void> {
  try {
    yield* put(PROJECT_LIST_UNFOLLOW_SUBMIT.pending());
    yield* call(logDebug, 'Unfollowing project...');

    const { project, user } = assertNotNull(
      yield* select(getProjectListUnfollowing),
      'Could not find project or/and user to unfollow',
    );

    yield* call(unfollowProject, project.id);

    const query = yield* select(getProjectListQuery);
    const projects = yield* call(loadProjectList, query);

    const data: ProjectListData = { projects, query };

    const authUser = yield* select(getLoggedInUser);
    const { found } = yield* call(applyProjectListQuery, data, authUser);

    if (found.length === 0) {
      const search = yield* call(buildProjectListQuery, { ...query, page: INTEGER_ONE });
      yield* call(navigate, replace({ pathname: '/projects', search: search.toString() }));
    } else {
      yield* put(PROJECT_LIST_FETCH.trigger(data));
    }

    yield* call(
      showNotification,
      {
        variant: 'success',
        type: 'done',
        messageId: 'projectList/notification/unfollow/success',
        messageValue: {
          projectName: project.data.title,
        },
      },
    );

    yield* call(logDebug, 'Unfollowing project... done', project, user);
    yield* put(PROJECT_LIST_UNFOLLOW_SUBMIT.success());
  } catch (error) {
    yield* call(sentryCatch, error);

    yield* call(showNotification, { variant: 'error', type: 'error' });

    yield* call(logError, 'Unfollowing project... error', error);
    yield* put(PROJECT_LIST_UNFOLLOW_SUBMIT.failure(NativeError.wrap(error)));
  }
}
